"use client";

import {
  type ChartColorLabels,
  ColorSwatches,
} from "@/components/tool-kit/charts/controls";
import {
  Panel,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { t } from "@/lib/i18n/interpolate";
import type { ChartColorId } from "@/lib/tool-kit/charts/color";
import type { VennMode } from "@/lib/tool-kit/diagrams/venn-input";

/*
 * The Venn diagram maker's form pieces: one panel per set (name, colour and, in "sets"
 * mode, its members) and the box for whatever lies outside every set. Every word comes
 * in through the widget's strings.
 */

export interface VennSetLabels extends ChartColorLabels {
  /** "Name of set {letter}" */
  setName: string;
  /** "Color of set {letter}" */
  setColor: string;
  /** "Members of {name}" */
  membersLabel: string;
}

export function VennSetPanel({
  letter,
  index,
  name,
  shownName,
  color,
  members,
  placeholder,
  mode,
  labels,
  onName,
  onColor,
  onMembers,
}: {
  letter: string;
  index: number;
  /** What the reader typed. */
  name: string;
  /** The name as drawn (the letter when the box is empty). */
  shownName: string;
  color: ChartColorId;
  members: string;
  /** Example members, shown while the box is empty. */
  placeholder: string;
  mode: VennMode;
  labels: VennSetLabels;
  onName: (value: string) => void;
  onColor: (value: ChartColorId) => void;
  onMembers: (value: string) => void;
}) {
  return (
    <Panel>
      <TextField
        id={`vdm-name-${index}`}
        label={t(labels.setName, { letter })}
        onChange={onName}
        value={name}
      />
      <ColorSwatches
        labels={labels}
        legend={t(labels.setColor, { letter })}
        name={`vdm-color-${index}`}
        onChange={onColor}
        value={color}
      />
      {mode === "sets" ? (
        <TextAreaField
          id={`vdm-members-${index}`}
          label={t(labels.membersLabel, { name: shownName })}
          onChange={onMembers}
          placeholder={placeholder}
          prose
          value={members}
        />
      ) : null}
    </Panel>
  );
}

export interface VennOutside {
  items: string;
  count: string;
  members: string;
}

export interface VennOutsideLabels {
  outsideCount: string;
  outsideItems: string;
  universeMembers: string;
  universeMembersHint: string;
}

/** What lies outside every circle: a count, a list of items, or the universal set's members. */
export function VennOutsideField({
  mode,
  outside,
  onChange,
  labels,
}: {
  mode: VennMode;
  outside: VennOutside;
  onChange: (patch: Partial<VennOutside>) => void;
  labels: VennOutsideLabels;
}) {
  if (mode === "counts") {
    return (
      <TextField
        id="vdm-outside-count"
        label={labels.outsideCount}
        numeric
        onChange={(count) => onChange({ count })}
        value={outside.count}
      />
    );
  }
  if (mode === "sets") {
    return (
      <TextAreaField
        hint={labels.universeMembersHint}
        id="vdm-universe-members"
        label={labels.universeMembers}
        onChange={(members) => onChange({ members })}
        prose
        value={outside.members}
      />
    );
  }
  return (
    <TextAreaField
      id="vdm-outside-items"
      label={labels.outsideItems}
      onChange={(items) => onChange({ items })}
      prose
      value={outside.items}
    />
  );
}
